import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { FaPlay, FaArrowRight, FaGlobeAsia, FaCertificate, FaCrown, FaUsers, FaVideo } from "react-icons/fa";
import { RiInstagramLine, RiYoutubeLine, RiFacebookLine } from "react-icons/ri";
// ─── SLIDES DATA ─────────────────────────────────────────────────────────────
const slides = [
  { id: 1, video: "/assets/video1.mp4", titleWhite: "Awaken Your", titleOrange: "Inner Balance", subtitle: "Premium international virtual yoga training platform designed for holistic wellness, mindfulness, meditation, and transformational living.", button: "Start Your Journey" },
  { id: 2, video: "/assets/video2.mp4", titleWhite: "Train Anytime", titleOrange: "Anywhere", subtitle: "Experience immersive live yoga classes across TV, laptop, and mobile with world class certified instructors.", button: "Explore Classes" },
  { id: 3, video: "/assets/video3.mp4", titleWhite: "Mind • Body", titleOrange: "& Soul", subtitle: "Join a global yoga and wellness ecosystem with meditation, breathing practices, chakra healing, and community support.", button: "Join Community" },
];
// ─── OPTIMIZED INDIVIDUAL VIDEO SLIDE COMPONENT ──────────────────────────────
// Keeps video elements mounted, plays active, pauses inactive. Avoids costly AnimatePresence unmounts.
const VideoSlide = ({ slide, isActive, shouldLoad }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;
    if (isActive) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn("Autoplay was prevented. Video will play on user interaction:", err);
        });
      }
    } else {
      video.pause();
    }
  }, [isActive, shouldLoad]);
  return (
    <motion.div
      initial={false}
      animate={{ opacity: isActive ? 1 : 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="absolute inset-0 w-full h-full z-[1]"
      style={{ pointerEvents: isActive ? "auto" : "none" }}
    >
      {shouldLoad && (
        <motion.div
          className="w-full h-full"
          animate={isActive ? { scale: [1, 1.08] } : { scale: 1 }}
          transition={{ duration: 10, ease: "linear" }}
        >
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="auto"
            poster="/assets/home_hero_bg.jpg"
            className="w-full h-full object-cover"
            style={{ willChange: "transform" }}
          >
            <source src={slide.video} type="video/mp4" />
          </video>
        </motion.div>
      )}
    </motion.div>
  );
};
const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(true);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [authState, setAuthState] = useState({ loggedIn: false, role: null });

  useEffect(() => {
    const token = localStorage.getItem("divyayoga_token");
    const role = (localStorage.getItem("divyayoga_role") || "").toLowerCase();
    setAuthState({
      loggedIn: !!token,
      role: role || "user",
    });
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => setShouldLoadVideo(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);
  // Auto-cycle slides every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((p) => (p + 1) % slides.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  // Seed floating particles once to prevent recalculating coordinates and glitching on re-renders
  const particles = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => ({
      id: i,
      width: `${(i % 3) + 2}px`,
      height: `${(i % 3) + 2}px`,
      top: `${((i * 17) % 80) + 10}%`,
      left: `${((i * 23) % 80) + 10}%`,
      duration: `${8 + (i % 6)}s`,
      delay: `${i * 0.4}s`,
    }));
  }, []);
  return (
    <>
      {/* Dynamic Keyframes injected locally to ensure 60fps animations without config edits */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin-clockwise {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-counter {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes float-bounce {
          0%, 100% { transform: translateY(-50%) translateY(0); }
          50% { transform: translateY(-50%) translateY(-12px); }
        }
        @keyframes scroll-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(12px); }
        }
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.15; }
          50% { transform: translateY(-30px) translateX(10px); opacity: 0.7; }
        }
        .animate-spin-slow {
          animation: spin-clockwise 28s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-counter 38s linear infinite;
        }
        .animate-float-bounce {
          animation: float-bounce 6s ease-in-out infinite;
        }
        .animate-scroll-bounce {
          animation: scroll-bounce 2s ease-in-out infinite;
        }
        .animate-float-particle {
          animation: float-particle var(--duration) ease-in-out infinite;
        }
      `}} />
      <section className="relative w-full min-h-screen overflow-hidden bg-[#0D0702] lg:py-14 py-12">
        {/* Base Hero Background Layer */}
        <div className="absolute inset-0 w-full h-full z-0">
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-[center_25%] sm:bg-center" 
            style={{ backgroundImage: "url('/assets/home_hero_bg.jpg')" }} 
          />
          {/* Subtle dark brown overlay on background image */}
          <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
        </div>
        {/* Render all background videos with conditional play states. 
            Pausing inactive videos preserves CPU/GPU resources, while keeping them 
            mounted prevents lag and network re-fetches during transitions. */}
        {!isMobile && (
          <div className="absolute inset-0 w-full h-full z-[1]">
            {slides.map((slide, index) => (
              <VideoSlide key={slide.id} slide={slide} isActive={current === index} shouldLoad={shouldLoadVideo} />
            ))}
          </div>
        )}
        {isMobile && (
          <div className="absolute inset-0 w-full h-full z-[1]">
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-[center_25%] sm:bg-center" 
              style={{ backgroundImage: "url('/assets/home_hero_bg.jpg')" }} 
            />
            {/* Subtle dark brown overlay on mobile background image */}
            <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
          </div>
        )}
        {/* Rich Royal Backdrop Gradients & Overlays (Balanced opacity for artwork visibility) */}
        <div className="absolute inset-0 z-10 bg-[#120700]/20 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#120700]/70 via-[#180902]/45 to-[#0D0500]/75 lg:bg-gradient-to-r lg:from-[#120700]/75 lg:via-[#180902]/45 lg:to-[#120700]/15 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_top,rgba(242,154,12,0.18)_0%,transparent_65%)] pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_bottom_left,rgba(242,154,12,0.12)_0%,transparent_50%)] pointer-events-none" />
        {/* 
          OPTIMIZATION: Replaced dynamic CSS blur filters (blur-[100px]) with high-performance 
          radial gradients. Animating filters requires the GPU to perform expensive convolutions 
          on every frame, whereas animating opacity on radial gradients runs at a native 60fps.
        */}
        <motion.div
          animate={{ opacity: [0.25, 0.45, 0.25] }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
          className="absolute top-[-220px] left-[-180px] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.3)_0%,transparent_70%)] z-10 pointer-events-none will-change-opacity"
        />
        <motion.div
          animate={{ opacity: [0.15, 0.28, 0.15] }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
          className="absolute bottom-[-220px] right-[-120px] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(255,122,0,0.2)_0%,transparent_70%)] z-10 pointer-events-none will-change-opacity"
        />
        <motion.div
          animate={{ opacity: [0.1, 0.2, 0.1] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
          className="absolute bottom-[-250px] left-[25%] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(104,138,94,0.15)_0%,transparent_70%)] z-10 pointer-events-none will-change-opacity"
        />
        {/* OPTIMIZATION: Swapped JS-animated rotating SVGs for native CSS keyframe spin animations */}
        <div className="absolute top-[8%] right-[10%] w-[500px] h-[500px] border border-white/10 rounded-full z-20 animate-spin-slow pointer-events-none will-change-transform" />
        <div className="absolute bottom-[5%] left-[5%] w-[350px] h-[350px] border border-[#F29A0C]/20 rounded-full z-20 animate-spin-slow-reverse pointer-events-none will-change-transform" />
        {/* 
          OPTIMIZATION: Seeded floating particles via useMemo. 
          Using Math.random() on render caused particles to jump positions and glitch when state updated.
          Also, we hide them on mobile (hidden md:block) to prevent mobile CPU overload.
        */}
        <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none hidden md:block">
          {particles.map((p) => (
            <span
              key={p.id}
              className="absolute rounded-full bg-white/30 animate-float-particle will-change-transform"
              style={{
                width: p.width,
                height: p.height,
                top: p.top,
                left: p.left,
                animationDelay: p.delay,
                "--duration": p.duration,
              }}
            />
          ))}
        </div>
        <div className="relative z-30 min-h-screen flex items-center px-6 lg:px-20 pt-[80px]">
          <div className="max-w-[760px]">
            {/* OPTIMIZATION: Replaced heavy transition blur filters (blur(20px)) with clean, 
                high-performance opacity and translateY (y) transitions. This eliminates text dragging. */}
            <AnimatePresence mode="wait">
              <motion.div
                key={slides[current].id}
                initial={{ opacity: 0, y: 40, clipPath: "inset(18% 0 0 0)" }}
                animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0 0)" }}
                exit={{ opacity: 0, y: -28, clipPath: "inset(0 0 18% 0)" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="will-change-transform"
              >
                <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-xl mb-6">
                  <div className="w-2 h-2 rounded-full bg-[#F29A0C] animate-pulse" />
                  <span className="text-white tracking-[4px] uppercase text-[10px]">DIVYYOG FOUNDATION</span>
                </div>
                
                <h1 className="text-[52px] md:text-[72px] xl:text-[82px] leading-[0.95] font-bold" style={{ fontFamily: "Poppins" }}>
                  <span className="text-white">{slides[current].titleWhite}</span><br />
                  <span className="text-gold-gradient">{slides[current].titleOrange}</span>
                </h1>
                
                <p className="text-[#F8F8F8] text-base md:text-[19px] leading-[34px] max-w-[640px] mt-6" style={{ fontFamily: "Inter" }}>
                  {slides[current].subtitle}
                </p>
                
                <div className="flex flex-wrap gap-4 mt-10">
                  <Link
                    to={
                      authState.loggedIn
                        ? authState.role.includes("admin")
                          ? "/admin/dashboard"
                          : "/user/dashboard"
                        : "/register"
                    }
                  >
                    <button className="btn-shine group relative overflow-hidden px-8 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white text-[15px] font-semibold shadow-[0_15px_40px_rgba(242,154,12,0.35)] hover:scale-105 transition-all duration-500 cursor-pointer">
                      <span className="relative z-10 flex items-center gap-3">
                        <FaPlay />
                        {authState.loggedIn ? "Go to Dashboard" : "Start Your Journey"}
                      </span>
                      <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-all duration-500" />
                    </button>
                  </Link>
                  <Link to="/courses">
                    <button className="px-8 py-4 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white text-[15px] font-medium hover:bg-white hover:text-black transition-all duration-500 cursor-pointer">
                      <span className="flex items-center gap-3">Explore Programs<FaArrowRight /></span>
                    </button>
                  </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
                  {[
                    [FaVideo, "Live Online\nYoga Sessions"],
                    [FaCertificate, "Certified\nTrainers"],
                    [FaGlobeAsia, "Multi Language\nSupport"],
                    [FaCrown, "Flexible\nPlans"],
                  ].map(([Icon, label], i) => (
                    <div key={i} className="group bg-white/10 backdrop-blur-2xl border border-white/10 rounded-[20px] px-4 py-4 hover:bg-white/15 hover:-translate-y-1 hover:border-[#F29A0C]/40 transition-all duration-500">
                      <Icon className="text-[#F29A0C] text-lg mb-3" />
                      <h3 className="text-white text-xs md:text-sm font-medium leading-[22px]">
                        {label.split("\n").map((l, j) => (
                          <React.Fragment key={j}>
                            {l}
                            {j === 0 && <br />}
                          </React.Fragment>
                        ))}
                      </h3>
                    </div>
                  ))}
                </div>

                {/* Mobile / Tablet Social Quick Links */}
                <div className="flex xl:hidden items-center gap-3 mt-6">
                  <span className="text-white/60 text-xs tracking-[2px] uppercase">Follow:</span>
                  {[
                    { Icon: RiInstagramLine, label: "Instagram", href: "https://www.instagram.com/divyyog.foundation" },
                    { Icon: RiYoutubeLine, label: "YouTube", href: "https://www.youtube.com/@divyogafoundation1169" },
                    { Icon: RiFacebookLine, label: "Facebook", href: "https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/" }
                  ].map(({ Icon, label, href }, i) => (
                    <a key={i} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-[#F29A0C] transition-all">
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        {/* OPTIMIZATION: Swapped JS animate for CSS bounce animation */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 z-30 hidden xl:block animate-float-bounce will-change-transform">
          <div className="bg-white/10 backdrop-blur-2xl border border-white/10 rounded-[28px] p-5 w-[240px]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#F3F3F3] text-xs tracking-[2px] uppercase">Trusted Platform</p>
                <h2 className="text-white text-3xl font-bold mt-2">10K+</h2>
                <p className="text-[#DDD] text-sm mt-1 leading-[24px]">Active Members<br />24/7 Live Sessions</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-xl">
                <FaUsers />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute right-8 bottom-10 z-40 hidden xl:flex flex-row gap-4">
          {[
            { Icon: RiInstagramLine, label: "Instagram", href: "https://www.instagram.com/divyyog.foundation" },
            { Icon: RiYoutubeLine, label: "YouTube", href: "https://www.youtube.com/@divyogafoundation1169" },
            { Icon: RiFacebookLine, label: "Facebook", href: "https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/" }
          ].map(({ Icon, label, href }, i) => (
            <a key={i} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white hover:bg-[#F29A0C] transition-all duration-500">
              <Icon size={20} />
            </a>
          ))}
        </div>
        {/* OPTIMIZATION: Swapped JS animate for CSS bounce animation */}
        <div className="absolute bottom-24 right-14 z-40 hidden lg:flex flex-row items-center gap-3 animate-scroll-bounce will-change-transform">
          <span className="text-white/70 tracking-[5px] text-[10px] uppercase">Scroll</span>
          <div className="w-[1px] h-16 bg-gradient-to-b from-[#F29A0C] to-transparent" />
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`transition-all duration-500 rounded-full ${current === i ? "w-16 h-3 bg-[#F29A0C]" : "w-3 h-3 bg-white/40"}`} />
          ))}
        </div>
      </section>
    </>
  );
};
export default HeroSection;
